import type { AISuggestedRiskControlsResult } from 'src/pages/controls/tab/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { useAISuggestedRiskControlsStore } from 'src/pages/controls/tab/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { v4 as uuidV4 } from 'uuid';

import type { AIWorkflowJobResult } from '@/components/ai-workflows/aiWorkflowStore';
import { useAIWorkflowService } from '@/components/ai-workflows/useAIWorkflowService';

// This must match the options defined on the AI Workflow API
type SuggestionMode = 'clean-run' | 'complement-existing';

interface SuggestControlsBody {
  riskId: string;
  suggestionMode: SuggestionMode;
  // no-dd-sa
  matchAgainstControlLibrary: boolean;
  libraryMatchThreshold: number;
  additionalContext: string | null;
}

const settingsSessionStorageKey = 'AI-Suggest-Controls-Settings';

function applyDefaultsForMissingControlValues(
  result: AIWorkflowJobResult<AISuggestedRiskControlsResult>
): AIWorkflowJobResult<AISuggestedRiskControlsResult> {
  if (result.result) {
    return {
      ...result,
      result: {
        metadata: result.result!.metadata,
        suggestedControls: result.result!.suggestedControls.map((ctrl) => {
          const { controlId, createdByUser, createdAtTimestamp, ...rest } =
            ctrl;

          return {
            ...rest,
            controlId: controlId ?? uuidV4(),
            createdByUser: createdByUser ?? 'AI',
            createdAtTimestamp: createdAtTimestamp ?? new Date().toISOString(),
          };
        }),
      },
    };
  }

  return result;
}

function buildSuggestControlsBody(riskId: string): () => SuggestControlsBody {
  ////////
  // Temporary code to allow the product team to play around with the configuration
  // by adjusting values in session storage
  let settings = sessionStorage.getItem(settingsSessionStorageKey);

  if (!settings) {
    settings = JSON.stringify({
      suggestionMode: 'clean-run',
      matchAgainstControlLibrary: true,
      libraryMatchThreshold: 0.75,
      additionalContext: null,
    });

    sessionStorage.setItem(settingsSessionStorageKey, settings);
  }

  const controlSettings = JSON.parse(settings);
  ////////

  return () => ({
    riskId,
    ...controlSettings,
  });
}

export function useAISuggestControls() {
  const { runWorkflow } = useAIWorkflowService<AISuggestedRiskControlsResult>(
    useAISuggestedRiskControlsStore(),
    applyDefaultsForMissingControlValues,
    'ai-engine/workflow-api/workflows/suggest-controls-by-risk-id'
  );

  async function suggestControls(riskId: string) {
    await runWorkflow(buildSuggestControlsBody(riskId));
  }

  return {
    suggestControls,
  };
}
